import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  Plus, RefreshCw, Trash2, Edit3, KeyRound, ShieldCheck, ShieldOff,
  Copy, Check, X, AlertCircle, Mail,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_PILL = {
  admin: 'bg-amber-100 text-amber-800 border-amber-200',
  staff: 'bg-sky-100 text-sky-800 border-sky-200',
};

const formatError = (err) => {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || 'Request failed';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(d);
};

const AdminUsers = () => {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null); // user object
  const [credentialReveal, setCredentialReveal] = useState(null); // { email, temp_password }
  const [busyId, setBusyId] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/admin/users`, { headers: authHeaders });
      setUsers(res.data.users || []);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { if (token) fetchUsers(); }, [token, fetchUsers]);

  const handleResetPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}? A new temp password will be generated and emailed.`)) return;
    setBusyId(u.id);
    try {
      const res = await axios.post(`${API_URL}/api/admin/users/${u.id}/reset-password`, {}, { headers: authHeaders });
      setCredentialReveal({ email: u.email, temp_password: res.data.temp_password, action: 'reset', sent: res.data.welcome_email_sent });
      await fetchUsers();
    } catch (e) {
      setError(formatError(e));
    } finally { setBusyId(null); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}? This cannot be undone.`)) return;
    setBusyId(u.id);
    try {
      await axios.delete(`${API_URL}/api/admin/users/${u.id}`, { headers: authHeaders });
      await fetchUsers();
    } catch (e) {
      setError(formatError(e));
    } finally { setBusyId(null); }
  };

  const handleToggleActive = async (u) => {
    setBusyId(u.id);
    try {
      await axios.put(`${API_URL}/api/admin/users/${u.id}`, { is_active: !u.is_active }, { headers: authHeaders });
      await fetchUsers();
    } catch (e) {
      setError(formatError(e));
    } finally { setBusyId(null); }
  };

  return (
    <AdminLayout>
      <div className="px-6 py-6 w-full min-w-0" data-testid="admin-users-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#1B3A5C]">Admin & Staff Users</h1>
            <p className="text-sm text-gray-500">Add, remove, and reset passwords for accounts with backend access.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowCreate(true)} data-testid="add-admin-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#F5A623] text-[#1B3A5C] font-bold text-sm rounded-full hover:bg-[#D88F1B]">
              <Plus className="w-4 h-4" /> Add Admin
            </button>
            <button onClick={fetchUsers} className="p-2 border border-gray-200 rounded-full hover:bg-gray-50" data-testid="refresh-users-btn">
              <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2" data-testid="users-error">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-gray-50 text-left text-gray-500 uppercase text-xs tracking-wider">
                <tr>
                  <th className="px-5 py-3 font-semibold">Name</th>
                  <th className="px-5 py-3 font-semibold">Email</th>
                  <th className="px-5 py-3 font-semibold">Role</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold">Created</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading && (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-gray-400">Loading users...</td></tr>
                )}
                {!loading && users.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-gray-400">No admin or staff users yet.</td></tr>
                )}
                {users.map((u) => {
                  const isSelf = currentUser?.email === u.email;
                  return (
                    <tr key={u.id} className="hover:bg-gray-50/60" data-testid={`user-row-${u.id}`}>
                      <td className="px-5 py-3 font-medium text-[#1B3A5C]">
                        {(u.first_name + ' ' + u.last_name).trim() || '-'}
                        {isSelf && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#F5A623] font-bold">You</span>}
                        {u.must_change_password && (
                          <span className="ml-2 inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">temp pwd</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-gray-700">{u.email}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${ROLE_PILL[u.role] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        {u.is_active ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-gray-400 text-xs font-medium">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-300" /> Disabled
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-gray-500 text-xs">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setEditing(u)} disabled={busyId === u.id}
                            className="p-1.5 rounded-lg text-gray-500 hover:text-[#1B3A5C] hover:bg-gray-100 disabled:opacity-40"
                            title="Edit" data-testid={`edit-user-${u.id}`}>
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleResetPassword(u)} disabled={busyId === u.id}
                            className="p-1.5 rounded-lg text-gray-500 hover:text-amber-700 hover:bg-amber-50 disabled:opacity-40"
                            title="Reset password" data-testid={`reset-pw-${u.id}`}>
                            <KeyRound className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleToggleActive(u)} disabled={busyId === u.id || isSelf}
                            className="p-1.5 rounded-lg text-gray-500 hover:text-sky-700 hover:bg-sky-50 disabled:opacity-40"
                            title={u.is_active ? 'Disable' : 'Enable'} data-testid={`toggle-active-${u.id}`}>
                            {u.is_active ? <ShieldOff className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                          </button>
                          <button onClick={() => handleDelete(u)} disabled={busyId === u.id || isSelf}
                            className="p-1.5 rounded-lg text-gray-500 hover:text-red-700 hover:bg-red-50 disabled:opacity-40"
                            title={isSelf ? 'You cannot delete yourself' : 'Delete'} data-testid={`delete-user-${u.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showCreate && (
        <UserFormModal mode="create" token={token} onClose={() => setShowCreate(false)}
          onCreated={(payload) => {
            setShowCreate(false);
            setCredentialReveal({ email: payload.user.email, temp_password: payload.temp_password, action: 'create', sent: payload.welcome_email_sent });
            fetchUsers();
          }} />
      )}

      {editing && (
        <UserFormModal mode="edit" token={token} user={editing} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchUsers(); }} />
      )}

      {credentialReveal && (
        <CredentialModal data={credentialReveal} onClose={() => setCredentialReveal(null)} />
      )}
    </AdminLayout>
  );
};

const UserFormModal = ({ mode, token, user, onClose, onCreated, onSaved }) => {
  const [form, setForm] = useState({
    email: user?.email || '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    role: user?.role || 'admin',
    is_active: user?.is_active ?? true,
    password: '',
    send_welcome: true,
    force_password_change: true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      if (mode === 'create') {
        const payload = {
          email: form.email.trim(),
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          phone: form.phone.trim(),
          role: form.role,
          send_welcome: form.send_welcome,
        };
        if (form.password.trim()) {
          payload.password = form.password.trim();
          payload.must_change_password = !!form.force_password_change;
        }
        const res = await axios.post(`${API_URL}/api/admin/users`, payload, { headers });
        onCreated && onCreated(res.data);
      } else {
        const payload = {
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          phone: form.phone.trim(),
          role: form.role,
          is_active: form.is_active,
        };
        await axios.put(`${API_URL}/api/admin/users/${user.id}`, payload, { headers });
        onSaved && onSaved();
      }
    } catch (e) {
      setErr(formatError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-[60]" data-testid="user-form-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-[#1B3A5C]">{mode === 'create' ? 'Add Admin or Staff' : `Edit ${user.email}`}</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {err && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{err}</span>
            </div>
          )}
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Email</label>
            <input type="email" required value={form.email} disabled={mode === 'edit'}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-200 disabled:bg-gray-50 disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30"
              data-testid="user-form-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">First name</label>
              <input type="text" required value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30"
                data-testid="user-form-first-name" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Last name</label>
              <input type="text" value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30"
                data-testid="user-form-last-name" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Phone</label>
            <input type="tel" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30"
              data-testid="user-form-phone" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30"
                data-testid="user-form-role">
                <option value="admin">Admin (full access)</option>
                <option value="staff">Staff (admin panel access)</option>
              </select>
            </div>
            {mode === 'edit' && (
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Status</label>
                <select value={form.is_active ? 'active' : 'disabled'} onChange={(e) => setForm({ ...form, is_active: e.target.value === 'active' })}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30"
                  data-testid="user-form-status">
                  <option value="active">Active</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>
            )}
          </div>
          {mode === 'create' && (
            <>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                  Temporary password <span className="text-gray-300 font-normal normal-case">(optional, auto-generated if blank)</span>
                </label>
                <input type="text" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Leave blank to auto-generate"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30 font-mono text-sm"
                  data-testid="user-form-password" />
                <p className="text-xs text-gray-400 mt-1">User will be required to change this on first login.</p>
              </div>
              <label className="flex items-start gap-2 cursor-pointer text-sm text-gray-700">
                <input type="checkbox" checked={form.send_welcome}
                  onChange={(e) => setForm({ ...form, send_welcome: e.target.checked })}
                  className="mt-0.5 accent-[#F5A623]"
                  data-testid="user-form-send-welcome" />
                <span>Send welcome email with login credentials</span>
              </label>
              {form.password.trim() && (
                <label className="flex items-start gap-2 cursor-pointer text-sm text-gray-700">
                  <input type="checkbox" checked={form.force_password_change}
                    onChange={(e) => setForm({ ...form, force_password_change: e.target.checked })}
                    className="mt-0.5 accent-[#F5A623]"
                    data-testid="user-form-force-pw-change" />
                  <span>Require user to change this password on first login</span>
                </label>
              )}
            </>
          )}
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100">Cancel</button>
          <button type="submit" disabled={busy} data-testid="user-form-submit"
            className="px-5 py-2 rounded-lg bg-[#F5A623] text-[#1B3A5C] font-bold text-sm hover:bg-[#D88F1B] disabled:opacity-50">
            {busy ? 'Saving...' : mode === 'create' ? 'Create User' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

const CredentialModal = ({ data, onClose }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(data.temp_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-[70]" data-testid="credential-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-5 bg-amber-50 border-b border-amber-100">
          <h2 className="text-lg font-bold text-[#1B3A5C] flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#F5A623]" />
            {data.action === 'create' ? 'User Created' : 'Password Reset'}
          </h2>
          <p className="text-sm text-amber-800 mt-1">
            {data.sent ? (
              <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Welcome email sent to <strong>{data.email}</strong></span>
            ) : (
              <span>Email delivery failed or was skipped. Share the password manually.</span>
            )}
          </p>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Email</label>
            <div className="px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-gray-800 text-sm">{data.email}</div>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Temporary password</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-[#1B3A5C] font-mono text-sm" data-testid="credential-temp-password">{data.temp_password}</code>
              <button onClick={copy} className="px-3 py-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 text-sm" data-testid="copy-temp-password">
                {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1.5">The user will be required to change this on first login. This is the only time you can view it.</p>
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
          <button onClick={onClose} className="px-5 py-2 rounded-lg bg-[#F5A623] text-[#1B3A5C] font-bold text-sm hover:bg-[#D88F1B]" data-testid="credential-close-btn">Done</button>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
